/* ==========================================================================
   HUSSIENI HOME — PRODUCT CONFIGURATOR BEHAVIOUR
   Loads only on product pages where the configurator is switched on.

   What this file is responsible for:
   - keeping the live summary, the chosen-option lines and the reference in
     step with the radios
   - enforcing compatibility rules, so a combination we cannot manufacture
     cannot be submitted
   - writing the hidden line item properties that carry codes, the frozen
     snapshot and the approval status onto the order
   - routing anything we cannot charge the standard price for to an enquiry
     instead of the cart
   - showing a preview ONLY when a photograph of that exact combination
     exists, and saying so plainly when one does not

   What it deliberately does not do: change the displayed price, fake a
   rotation, tint an image to imply a colour, or claim a 3D view. A real 3D
   path needs a prepared model with independently addressable parts; no
   product in the catalogue has one yet, so no such control is rendered.
   ========================================================================== */
(function () {
  'use strict';

  var INIT_FLAG = 'hhCfgReady';

  /* Deterministic short reference, so the same selection always produces the
     same code on the order rather than a new one on every page view. */
  function reference(seed) {
    var h = 5381;
    for (var i = 0; i < seed.length; i++) {
      h = ((h << 5) + h + seed.charCodeAt(i)) | 0;
    }
    return 'HH-' + Math.abs(h).toString(36).toUpperCase().padStart(6, '0').slice(0, 7);
  }

  function parseConfig(root) {
    var node = root.querySelector('[data-hh-cfg-data]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      // A malformed payload must not take the page down. The radios still
      // submit their own values, so the customer can still order.
      return null;
    }
  }

  function init(root) {
    if (root.dataset[INIT_FLAG] === '1') return;
    var cfg = parseConfig(root);
    if (!cfg) return;
    root.dataset[INIT_FLAG] = '1';

    var form = root.closest('form');
    var hiddenHost = root.querySelector('[data-hh-cfg-hidden]');
    var summaryList = root.querySelector('[data-hh-cfg-summary-list]');
    var refOut = root.querySelector('[data-hh-cfg-ref]');
    var resetBtn = root.querySelector('[data-hh-cfg-reset]');
    var quoteBox = root.querySelector('[data-hh-cfg-quote]');
    var quoteMsg = root.querySelector('[data-hh-cfg-quote-msg]');
    var quoteLink = root.querySelector('[data-hh-cfg-quote-link]');
    var previewImg = root.querySelector('[data-hh-cfg-preview-img]');
    var previewFallback = root.querySelector('[data-hh-cfg-preview-fallback]');
    var previewMsg = root.querySelector('[data-hh-cfg-preview-msg]');
    var sizeToggle = root.querySelector('[data-hh-cfg-customsize]');
    var sizeBox = root.querySelector('[data-hh-cfg-sizebox]');
    var sizeNote = root.querySelector('[data-hh-cfg-sizenote]');
    var submitBtn = form ? form.querySelector('button[name="add"]') : null;

    /* ------------------------------------------------------------------
       Selection state
       ------------------------------------------------------------------ */
    function currentSelection() {
      var out = {};
      cfg.groups.forEach(function (g) {
        var checked = root.querySelector(
          '[data-hh-cfg-group="' + g.key + '"] .hh-cfg__radio:checked'
        );
        out[g.key] = checked ? checked.dataset.hhCfgOption : null;
      });
      return out;
    }

    /* ------------------------------------------------------------------
       Compatibility rules
       A rule says: when option A is chosen, these others cannot be made.
       Blocked options are disabled and labelled with the stated reason, and
       if the blocked option happened to be selected its group falls back to
       its default, so the form never holds an unmakeable combination.
       ------------------------------------------------------------------ */
    function applyRules(sel) {
      var blocked = {};
      (cfg.rules || []).forEach(function (rule) {
        var active = Object.keys(sel).some(function (k) { return sel[k] === rule.a; });
        if (!active) return;
        (rule.blocks || []).forEach(function (h) {
          blocked[h] = rule.reason || cfg.strings.ruleBlocked;
        });
      });

      var changed = false;
      root.querySelectorAll('[data-hh-cfg-swatch]').forEach(function (sw) {
        var handle = sw.dataset.hhCfgSwatch;
        var radio = sw.querySelector('.hh-cfg__radio');
        var pending = sw.classList.contains('is-pending');
        var isBlocked = Object.prototype.hasOwnProperty.call(blocked, handle);

        sw.classList.toggle('is-blocked', isBlocked);
        if (!pending && radio) radio.disabled = isBlocked;

        var flag = sw.querySelector('[data-hh-cfg-rule-flag]');
        if (isBlocked) {
          if (!flag) {
            flag = document.createElement('span');
            flag.className = 'hh-cfg__flag';
            flag.setAttribute('data-hh-cfg-rule-flag', '');
            var meta = sw.querySelector('.hh-cfg__meta');
            if (meta) meta.appendChild(flag);
          }
          flag.textContent = blocked[handle];
          if (radio && radio.checked) {
            radio.checked = false;
            changed = true;
          }
        } else if (flag) {
          flag.remove();
        }
      });

      // Anything that lost its selection falls back to its default option.
      if (changed) {
        cfg.groups.forEach(function (g) {
          var scope = root.querySelector('[data-hh-cfg-group="' + g.key + '"]');
          if (!scope) return;
          if (scope.querySelector('.hh-cfg__radio:checked')) return;
          var fallback =
            scope.querySelector('.hh-cfg__radio[data-hh-cfg-option="' + g.default + '"]:not([disabled])') ||
            scope.querySelector('.hh-cfg__radio:not([disabled])');
          if (fallback) fallback.checked = true;
        });
      }
      return changed;
    }

    /* ------------------------------------------------------------------
       Hidden line item properties
       Rebuilt from scratch on every change, so a stale code can never be
       left behind attached to a different option.

       The chosen option's NAME is already submitted by the radio itself,
       which is what keeps the page working without JavaScript. Writing it
       again here would put two inputs with the same name in the form, so
       only the data the radio cannot carry is added.
       ------------------------------------------------------------------ */
    function setHidden(pairs) {
      if (!hiddenHost) return;
      hiddenHost.textContent = '';
      Object.keys(pairs).forEach(function (name) {
        var value = pairs[name];
        if (value === null || value === undefined || value === '') return;
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[' + name + ']';
        input.value = value;
        hiddenHost.appendChild(input);
      });
    }

    /* ------------------------------------------------------------------
       Preview
       Keys are group_key:option-handle pairs, sorted by group key and joined
       with a pipe. A combination with no entry shows the original photograph
       with an explicit note that it does not represent the current choice.
       ------------------------------------------------------------------ */
    function previewKey(sel) {
      return Object.keys(sel)
        .filter(function (k) { return sel[k]; })
        .sort()
        .map(function (k) { return k + ':' + sel[k]; })
        .join('|');
    }

    function updatePreview(sel) {
      if (!previewMsg) return;
      var match = (cfg.previews || {})[previewKey(sel)];
      if (match && match.url) {
        if (previewImg) {
          previewImg.src = match.url;
          previewImg.alt = match.alt || cfg.productTitle;
          previewImg.hidden = false;
        }
        if (previewFallback) previewFallback.hidden = true;
        previewMsg.textContent = cfg.strings.previewMatch;
      } else {
        if (previewImg) {
          previewImg.hidden = true;
          previewImg.removeAttribute('src');
        }
        if (previewFallback) previewFallback.hidden = false;
        previewMsg.textContent =
          cfg.strings.previewUnavailable + ' ' + cfg.strings.previewOriginalNote;
      }
    }

    /* ------------------------------------------------------------------
       The one function that keeps everything consistent
       ------------------------------------------------------------------ */
    function sync() {
      var sel = currentSelection();
      if (applyRules(sel)) sel = currentSelection();

      var props = {};
      var summary = [];
      var quoteReasons = [];
      var missingRequired = false;
      var snapshot = { product: cfg.productTitle, selections: [] };

      cfg.groups.forEach(function (g) {
        var handle = sel[g.key];
        var chosenLine = root.querySelector('[data-hh-cfg-chosen="' + g.key + '"]');
        var data = handle ? g.options[handle] : null;

        if (!data) {
          if (g.required) missingRequired = true;
          if (chosenLine) chosenLine.textContent = cfg.strings.notSelected;
          summary.push([g.label, cfg.strings.notSelected]);
          return;
        }

        var label = data.name + (data.code ? ' · ' + data.code : '');
        if (chosenLine) chosenLine.textContent = label;
        summary.push([g.label, label]);

        // The name travels on the radio; only the code needs adding.
        if (g.codeProp && data.code) props[g.codeProp] = data.code;

        snapshot.selections.push({
          group: g.label,
          part: g.part || null,
          option: data.name,
          family: data.family || null,
          code: data.code || null,
          colourCode: data.colourCode || null
        });

        if (data.priceMode === 'quote_required') {
          quoteReasons.push(cfg.strings.quoteOption.replace('[option]', data.name));
        }
      });

      // Size. A custom size is always an enquiry, never checkout at the
      // standard published price.
      var customSize = !!(sizeToggle && sizeToggle.checked);
      if (cfg.customSizeEnabled) {
        if (customSize) {
          var note = sizeNote && sizeNote.value.trim();
          props[cfg.strings.customSizeProp] = note || 'Requested — dimensions to be confirmed';
          summary.push([cfg.strings.customSizeProp, note || '—']);
          quoteReasons.push(cfg.strings.quoteSize);
          snapshot.customSize = note || null;
        } else {
          props[cfg.strings.standardSizeProp] = cfg.strings.standardSizeValue;
          summary.push([cfg.strings.standardSizeProp, cfg.strings.standardSizeValue]);
        }
      }

      // A required group with nothing orderable in it blocks the sale outright.
      if (cfg.blockedGroups > 0 || missingRequired) {
        quoteReasons.push(cfg.strings.quoteBlocked);
      }

      // Reference and the frozen snapshot, so the order keeps the names and
      // codes as they were when it was placed even if the material library is
      // edited later.
      var seed = cfg.productId + '|' + previewKey(sel) + '|' + (customSize ? 'custom' : 'standard');
      var ref = reference(seed);
      props[cfg.strings.refProp] = ref;
      props[cfg.strings.approvalProp] = cfg.strings.approvalStatus;
      snapshot.reference = ref;
      snapshot.capturedAt = new Date().toISOString();
      props['_hh_configuration'] = JSON.stringify(snapshot);

      setHidden(props);

      if (summaryList) {
        summaryList.textContent = '';
        summary.forEach(function (row) {
          var dt = document.createElement('dt');
          dt.textContent = row[0];
          var dd = document.createElement('dd');
          dd.textContent = row[1];
          summaryList.appendChild(dt);
          summaryList.appendChild(dd);
        });
      }
      if (refOut) refOut.textContent = ref;

      updatePreview(sel);

      /* Enquiry versus cart. The displayed price is never touched; we simply
         refuse to sell a configuration at a price that has not been agreed. */
      var needsQuote = quoteReasons.length > 0;
      if (quoteBox) quoteBox.hidden = !needsQuote;
      if (needsQuote) {
        if (quoteMsg) quoteMsg.textContent = quoteReasons.join(' ');
        if (quoteLink) {
          var url = new URL(root.dataset.quoteUrl, window.location.origin);
          url.searchParams.set('ref', ref);
          url.searchParams.set('piece', cfg.productTitle);
          quoteLink.href = url.pathname + url.search;
        }
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.setAttribute('aria-disabled', 'true');
          submitBtn.hidden = true;
        }
      } else {
        // Leave nothing stale behind the hidden panel.
        if (quoteMsg) quoteMsg.textContent = '';
        if (quoteLink) quoteLink.href = '#';
        if (submitBtn) {
          submitBtn.hidden = false;
          submitBtn.removeAttribute('aria-disabled');
          submitBtn.disabled = false;
        }
      }

      if (resetBtn) resetBtn.hidden = false;
    }

    /* ------------------------------------------------------------------
       Listeners. One delegated handler per root, so a section reload in the
       theme editor cannot stack duplicates.
       ------------------------------------------------------------------ */
    root.addEventListener('change', function (e) {
      if (e.target.matches('.hh-cfg__radio')) { sync(); return; }
      if (sizeToggle && e.target === sizeToggle) {
        if (sizeBox) sizeBox.hidden = !sizeToggle.checked;
        sync();
      }
    });

    if (sizeNote) {
      sizeNote.addEventListener('input', function () { sync(); });
    }

    if (resetBtn) {
      resetBtn.addEventListener('click', function () {
        cfg.groups.forEach(function (g) {
          var scope = root.querySelector('[data-hh-cfg-group="' + g.key + '"]');
          if (!scope) return;
          var target = scope.querySelector(
            '.hh-cfg__radio[data-hh-cfg-option="' + g.default + '"]:not([disabled])'
          );
          if (target) target.checked = true;
        });
        if (sizeToggle) {
          sizeToggle.checked = false;
          if (sizeBox) sizeBox.hidden = true;
        }
        if (sizeNote) sizeNote.value = '';
        sync();
      });
    }

    /* Swatch zoom, so the weave can actually be looked at. */
    var lightbox = document.querySelector('[data-hh-cfg-lightbox]');
    if (lightbox) {
      var lbImg = lightbox.querySelector('[data-hh-cfg-lightbox-img]');
      var lbTitle = lightbox.querySelector('[data-hh-cfg-lightbox-title]');
      var lastFocus = null;

      var closeLightbox = function () {
        lightbox.hidden = true;
        if (lbImg) lbImg.removeAttribute('src');
        if (lastFocus) lastFocus.focus();
      };

      root.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-hh-cfg-zoom]');
        if (!btn) return;
        lastFocus = btn;
        if (lbImg) {
          lbImg.src = btn.dataset.hhCfgZoom;
          lbImg.alt = btn.dataset.zoomTitle || '';
        }
        if (lbTitle) lbTitle.textContent = btn.dataset.zoomTitle || '';
        lightbox.hidden = false;
        var close = lightbox.querySelector('[data-hh-cfg-lightbox-close]');
        if (close) close.focus();
      });

      lightbox.addEventListener('click', function (e) {
        if (e.target === lightbox || e.target.closest('[data-hh-cfg-lightbox-close]')) {
          closeLightbox();
        }
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !lightbox.hidden) closeLightbox();
      });
    }

    sync();
  }

  function boot() {
    document.querySelectorAll('[data-hh-cfg]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // The theme editor replaces section markup wholesale; the new root arrives
  // without the flag, so it initialises once and the old one is gone.
  document.addEventListener('shopify:section:load', boot);
})();